import { History, Visibility } from "@mui/icons-material";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import type { DocumentReissueHistoryItem } from "./documentReissueChain";

type DocumentReissueHistoryDialogProps = {
  open: boolean;
  onClose: () => void;
  history?: DocumentReissueHistoryItem[];
  title?: string;
  /** Abre o visualizador do PDF (mesmo fluxo do IframeDialog). */
  onViewBinary?: (binaryUrl: string) => void;
};

/**
 * Histórico simples das remissões de um documento (cadeia relatesTo/replaces).
 */
export function DocumentReissueHistoryDialog({
  open,
  onClose,
  history = [],
  title = "Histórico de remissões",
  onViewBinary,
}: DocumentReissueHistoryDialogProps) {
  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>
        <Stack direction="row" alignItems="center" spacing={1}>
          <History color="primary" />
          <Typography variant="h6" component="span" fontSize="1.1rem">
            {title}
          </Typography>
        </Stack>
      </DialogTitle>
      <DialogContent dividers>
        {!history.length ? (
          <Typography variant="body2" color="text.secondary">
            Nenhuma remissão encontrada para este documento.
          </Typography>
        ) : (
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Versão</TableCell>
                <TableCell>Data</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="center">Ação</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {[...history]
                .map((item, originalIndex) => ({ item, originalIndex }))
                .reverse()
                .map(({ item, originalIndex }) => {
                  const versionLabel = item.isLatest
                    ? "Atual"
                    : item.isFirst
                      ? "Original"
                      : `Remissão ${originalIndex}`;

                  const statusLabel =
                    item.status === "current"
                      ? "Vigente"
                      : item.status === "superseded"
                        ? "Substituído"
                        : item.status || "—";

                  return (
                    <TableRow key={item.id || `${item.date}-${originalIndex}`} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight={600}>
                          {versionLabel}
                        </Typography>
                      </TableCell>
                      <TableCell>{item.date}</TableCell>
                      <TableCell>{statusLabel}</TableCell>
                      <TableCell align="center">
                        {item.binaryUrl && onViewBinary ? (
                          <Tooltip title="Visualizar documento">
                            <IconButton
                              aria-label="Visualizar documento"
                              onClick={() => onViewBinary(item.binaryUrl!)}
                            >
                              <Visibility />
                            </IconButton>
                          </Tooltip>
                        ) : (
                          "—"
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
            </TableBody>
          </Table>
        )}
      </DialogContent>
    </Dialog>
  );
}
